// frontend/src/pages/IdpPage.js
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import IdpUploadZone from '../components/idp/IdpUploadZone';
import IdpResultPanel from '../components/idp/IdpResultPanel';
import IdpClassesMultiselect from '../components/idp/IdpClassesMultiselect';
import IdpProcessingCard from '../components/idp/IdpProcessingCard';
import HubCustomSelect from '../components/common/HubCustomSelect';
import { supportedFile } from '../utils/idp/supportedUpload';
import { messageFromStructuredApiError } from '../utils/idp/structuredApiError';
import { buildProcessDocumentCurl } from '../utils/idp/buildProcessDocumentCurl';
import { IdpServiceError, processDocument } from '../services/idpService';

const THRESHOLD_OPTIONS = [
  { value: '0.75', labelKey: 'idpPage.form.thresholdOptions.p75' },
  { value: '0.80', labelKey: 'idpPage.form.thresholdOptions.p80' },
  { value: '0.85', labelKey: 'idpPage.form.thresholdOptions.p85' },
  { value: '0.90', labelKey: 'idpPage.form.thresholdOptions.p90' },
  { value: '0.95', labelKey: 'idpPage.form.thresholdOptions.p95' },
];

const LOCALE_OPTIONS = [
  { value: 'pt-BR', labelKey: 'idpPage.form.localeOptions.ptBr' },
  { value: 'en', labelKey: 'idpPage.form.localeOptions.en' },
  { value: 'es', labelKey: 'idpPage.form.localeOptions.es' },
];

function mapStatusToUi(status) {
  if (status === 'SUCCESS') return 'success';
  if (status === 'PARTIAL_SUCCESS') return 'partial_success';
  return 'failed';
}

function formatThresholdPercent(threshold) {
  if (!Number.isFinite(threshold)) return '—';
  return `${Math.round(threshold * 100)}%`;
}

function IdpPage() {
  const { t, i18n } = useTranslation();
  const progressTimerRef = useRef(null);
  const [file, setFile] = useState(null);
  const [expectedClasses, setExpectedClasses] = useState(['nota_fiscal']);
  const [threshold, setThreshold] = useState('0.75');
  const [locale, setLocale] = useState('pt-BR');
  const [preprocessProfile, setPreprocessProfile] = useState('default');
  const [markHandwritingHints, setMarkHandwritingHints] = useState(false);
  const [skipPreprocessing, setSkipPreprocessing] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [progressPercent, setProgressPercent] = useState(0);
  const [clientError, setClientError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [result, setResult] = useState(null);
  const [formSnapshot, setFormSnapshot] = useState(null);
  const [lastCurl, setLastCurl] = useState(null);
  const [lastRawResponse, setLastRawResponse] = useState(null);

  const thresholdValue = useMemo(() => Number.parseFloat(threshold), [threshold]);
  const busy = phase === 'uploading' || phase === 'processing';
  const showForm = (phase === 'idle' || phase === 'failed') && !result;
  const showProcessing = busy;
  const showResult = Boolean(result) && !busy;

  const thresholdSelectOptions = useMemo(
    () => THRESHOLD_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(opt.labelKey),
    })),
    [t],
  );

  const localeSelectOptions = useMemo(
    () => LOCALE_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(opt.labelKey),
    })),
    [t],
  );

  const preprocessSelectOptions = useMemo(
    () => [
      { value: 'default', label: t('idpPage.form.preprocessProfileDefault') },
      { value: 'handwriting', label: t('idpPage.form.preprocessProfileHandwriting') },
      { value: 'print', label: t('idpPage.form.preprocessProfilePrint') },
    ],
    [t],
  );

  const canSubmit = Boolean(file) && expectedClasses.length > 0 && !busy;

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearProgressTimer(), [clearProgressTimer]);

  useEffect(() => {
    if (!busy) return undefined;
    setProgressPercent(0);
    clearProgressTimer();
    progressTimerRef.current = window.setInterval(() => {
      setProgressPercent((prev) => {
        if (prev >= 90) return prev;
        return prev + 2;
      });
    }, 400);
    return clearProgressTimer;
  }, [busy, clearProgressTimer]);

  const resetToForm = useCallback(() => {
    clearProgressTimer();
    setPhase('idle');
    setProgressPercent(0);
    setClientError(null);
    setUploadError(null);
    setFile(null);
    setResult(null);
    setFormSnapshot(null);
    setLastCurl(null);
    setLastRawResponse(null);
  }, [clearProgressTimer]);

  async function onSubmit(e) {
    e.preventDefault();
    setClientError(null);
    setUploadError(null);
    setResult(null);
    setFormSnapshot(null);
    setLastCurl(null);
    setLastRawResponse(null);

    if (!file) {
      setUploadError(t('idpPage.validation.selectFile'));
      return;
    }
    if (!supportedFile(file)) {
      setUploadError(t('idpPage.validation.unsupportedType'));
      return;
    }
    if (expectedClasses.length === 0) {
      setUploadError(t('idpPage.validation.oneClass'));
      return;
    }
    if (!Number.isFinite(thresholdValue) || thresholdValue < 0 || thresholdValue > 1) {
      setUploadError(t('idpPage.validation.thresholdRange'));
      return;
    }

    const snapshot = {
      fileName: file.name,
      expectedClasses: [...expectedClasses],
      threshold: thresholdValue,
      locale,
      preprocessProfile,
      skipPreprocessing,
      markHandwritingHints,
    };
    setFormSnapshot(snapshot);

    setPhase('uploading');
    try {
      setPhase('processing');
      const options = { locale };
      if (skipPreprocessing) {
        options.skipPreprocessing = true;
      } else if (preprocessProfile !== 'default') {
        options.preprocessProfile = preprocessProfile;
      }
      if (markHandwritingHints) {
        options.markHandwritingHintsInLlm = true;
      }

      const requestInput = {
        file,
        mimeType: file.type || 'application/octet-stream',
        expectedClasses,
        classificationThreshold: thresholdValue,
        options,
      };

      setLastCurl(buildProcessDocumentCurl(requestInput));

      const { data, rawBody } = await processDocument(requestInput);
      setProgressPercent(100);
      setLastRawResponse(rawBody);
      setResult(data);
      setPhase(mapStatusToUi(data.status));
    } catch (err) {
      setProgressPercent(100);
      setPhase('failed');
      const msg = (() => {
        if (err instanceof IdpServiceError) {
          if (err.code === 'cancelled') return t('idpPage.errors.cancelled');
          if (err.code === 'timeout') {
            return t('idpPage.errors.timeout', { seconds: err.timeoutSeconds ?? 0 });
          }
          if (err.code === 'http_error') {
            const structured = err.responseBody
              && messageFromStructuredApiError(err.responseBody, t);
            if (structured) return structured;
            return err.message || t('idpPage.errors.requestFailed', { status: err.status ?? 0 });
          }
        }
        return err instanceof Error ? err.message : t('idpPage.errors.unknown');
      })();
      setClientError(msg);
      if (err instanceof IdpServiceError && typeof err.responseBody === 'string') {
        setLastRawResponse(err.responseBody);
      } else {
        setLastRawResponse(msg);
      }
    } finally {
      clearProgressTimer();
    }
  }

  function exportJson() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = t('idpPage.result.exportFilename');
    a.click();
    URL.revokeObjectURL(url);
  }

  const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';

  return (
    <div key={i18n.language} className="idp-page">
      <header className="idp-page__header">
        <h1 className="idp-page__title">{t('navigation.idp')}</h1>
        <p className="idp-page__tagline">{t('idpPage.tagline')}</p>
      </header>

      {showForm ? (
        <form className="idp-page__grid" onSubmit={onSubmit}>
          <div className="idp-card idp-card--upload">
            <IdpUploadZone
              disabled={busy}
              file={file}
              onFile={(f) => {
                setFile(f);
                setUploadError(null);
              }}
              onUnsupportedFile={() => setUploadError(t('idpPage.upload.invalidTypeDrop'))}
              error={uploadError}
            />
          </div>

          <div className="idp-card idp-card--settings">
            <h3 className="idp-section-title">{t('idpPage.sections.settingsTitle')}</h3>

            <div className="idp-form-field">
              <label className="idp-form-field__title" htmlFor="idp-expected-classes">
                {t('idpPage.form.expectedClassesLabel')}
              </label>
              <p className="idp-field-hint idp-field-hint--compact">
                {t('idpPage.sections.expectedClassesHelp')}
              </p>
              <IdpClassesMultiselect
                value={expectedClasses}
                onChange={setExpectedClasses}
                disabled={busy}
              />
            </div>

            <div className="idp-form-row idp-form-row--three-cols">
              <div className="idp-form-field">
                <label className="idp-form-field__title" htmlFor="idp-threshold">
                  {t('idpPage.form.thresholdLabel')}
                </label>
                <HubCustomSelect
                  id="idp-threshold"
                  value={threshold}
                  onChange={setThreshold}
                  options={thresholdSelectOptions}
                  ariaLabel={t('idpPage.form.thresholdLabel')}
                  disabled={busy}
                />
              </div>
              <div className="idp-form-field">
                <label className="idp-form-field__title" htmlFor="idp-locale">
                  {t('idpPage.form.localeLabel')}
                </label>
                <HubCustomSelect
                  id="idp-locale"
                  value={locale}
                  onChange={setLocale}
                  options={localeSelectOptions}
                  ariaLabel={t('idpPage.form.localeLabel')}
                  disabled={busy}
                />
              </div>
              <div className="idp-form-field">
                <label className="idp-form-field__title" htmlFor="idp-preprocess">
                  {t('idpPage.form.preprocessProfileLabel')}
                </label>
                <HubCustomSelect
                  id="idp-preprocess"
                  value={preprocessProfile}
                  onChange={setPreprocessProfile}
                  options={preprocessSelectOptions}
                  ariaLabel={t('idpPage.form.preprocessProfileLabel')}
                  disabled={busy || skipPreprocessing}
                />
              </div>
            </div>

            <div className="idp-form-row idp-form-row--options">
              <label className="idp-option-card" htmlFor="idp-skip-preprocess">
                <input
                  id="idp-skip-preprocess"
                  type="checkbox"
                  checked={skipPreprocessing}
                  onChange={(e) => setSkipPreprocessing(e.target.checked)}
                  disabled={busy}
                />
                <span className="idp-option-card__label">{t('idpPage.form.skipPreprocessingLabel')}</span>
                <Tooltip title={t('idpPage.form.tooltips.skipPreprocessing')} arrow>
                  <span className="idp-option-card__info" tabIndex={0}>
                    <InfoOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                  </span>
                </Tooltip>
              </label>
              <label className="idp-option-card" htmlFor="idp-handwriting-hints">
                <input
                  id="idp-handwriting-hints"
                  type="checkbox"
                  checked={markHandwritingHints}
                  onChange={(e) => setMarkHandwritingHints(e.target.checked)}
                  disabled={busy}
                />
                <span className="idp-option-card__label">{t('idpPage.form.markHandwritingHintsLabel')}</span>
                <Tooltip title={t('idpPage.form.tooltips.markHandwritingHints')} arrow>
                  <span className="idp-option-card__info" tabIndex={0}>
                    <InfoOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                  </span>
                </Tooltip>
              </label>
            </div>

            <button
              className="idp-btn idp-btn--primary idp-btn--submit"
              type="submit"
              disabled={!canSubmit}
            >
              {busy ? (
                <>
                  <CircularProgress size={18} color="inherit" aria-hidden />
                  {t('idpPage.form.submitting')}
                </>
              ) : (
                t('idpPage.form.submit')
              )}
            </button>
          </div>
        </form>
      ) : null}

      {showProcessing ? (
        <IdpProcessingCard
          progressPercent={progressPercent}
          errorMessage={phase === 'failed' ? clientError : null}
        />
      ) : null}

      {clientError && !showProcessing && !showResult ? (
        <p className="idp-error-text idp-page__error">{clientError}</p>
      ) : null}

      {showResult ? (
        <IdpResultPanel
          result={result}
          formSnapshot={formSnapshot}
          phase={phase}
          onExportJson={exportJson}
          onChangeFile={resetToForm}
        />
      ) : null}

      {lastCurl && isDev ? (
        <details className="idp-api-debug idp-card idp-card--debug">
          <summary className="idp-api-debug__summary">{t('idpPage.debug.sectionTitle')}</summary>
          <div className="idp-form-field">
            <span className="idp-form-field__title">{t('idpPage.debug.curlLabel')}</span>
            <small className="idp-field-hint">{t('idpPage.debug.curlHint')}</small>
            <pre className="idp-pre-block idp-pre-block--debug idp-pre-block--tech" tabIndex={0}>
              {lastCurl}
            </pre>
          </div>
          <div className="idp-form-field">
            <span className="idp-form-field__title">{t('idpPage.debug.responseLabel')}</span>
            <small className="idp-field-hint">{t('idpPage.debug.responseHint')}</small>
            <pre className="idp-pre-block idp-pre-block--debug idp-pre-block--tech" tabIndex={0}>
              {lastRawResponse ?? ''}
            </pre>
          </div>
        </details>
      ) : null}
    </div>
  );
}

export { formatThresholdPercent };
export default IdpPage;
